import { CSSProperties, ReactNode, useMemo, useRef } from 'react';

import { useAuth } from '@r123/auth';
import { CobrokeBadge, CobrokeBadgeType } from '@r123/cobroke';
import { useTracker } from '@r123/segment-tracker';
import { useLocale } from 'next-intl';
import { useSearchParams } from 'next/navigation';

import { LangType } from '@/app/[lang]/_type/lang';
import Chip from '@/app/_components/Chip/Chip';
import ChipAssetBank from '@/app/_components/Chip/ChipAssetBank';
import ChipUnderNjop from '@/app/_components/Chip/ChipUnderNjop';
import {
    AdditionalSuggestionKind,
    SEARCHBAR_TOKENS_KEY,
} from '@/app/_constant/localStorageConstant';
import { DEFAULT_NPL_FLAG } from '@/app/_constant/srpConstant';
import { SubscriptionTierConstant } from '@/app/_constant/subscription-tier.constant';
import { cn } from '@/app/_helper/tailwind';
import useLocalStorageState from '@/app/_hooks/useLocalStorage';
import { useMediaQuery } from '@/app/_hooks/useMediaQuery';
import {
    Flag,
    Maybe,
    Property,
    PropertyAttributes,
    SuggestionKind,
} from '@/modules/core/gql/generated';
import { PropertyTypeEnum } from '@/modules/property/property.type';
import { SegmentTrackerEventEnum } from '@/modules/segment/segment.interface';

import { under1YearBadgesValue } from '../../shared/helper/card.helper';
import Under1YearBadge from './Under1YearBadge';

type PropertyInfoChipsProps = {
    additionalFlags?: Property['additionalFlags'];
    propertyType: Property['propertyType'];
    specialFlag: Property['specialFlag'];
    subscriptionTierId?: Property['subscriptionTypeId'];
    priceType?: Property['priceType'];
    isGts?: boolean;
    className?: string;
    children?: ReactNode;
    isAddChipVilla?: boolean;
    url: Maybe<string> | undefined;
    size?: 'default' | 'small';
    attributes?: PropertyAttributes;
    cobrokeBadge?: {
        badgeType?: CobrokeBadgeType;
        style?: CSSProperties;
        onBadgeClicked: () => void;
    };
    enableQuickLabelChip?: boolean;
};

const chipSize = {
    default: 'text-xs',
    small: 'text-3xs',
};

const PropertyInfoChips = ({
    additionalFlags,
    propertyType,
    specialFlag,
    subscriptionTierId = -1,
    priceType,
    isGts = false,
    className,
    children,
    attributes,
    url,
    size = 'default',
    cobrokeBadge,
    enableQuickLabelChip = true,
}: PropertyInfoChipsProps) => {
    const ref = useRef(null);
    const locale = useLocale();
    const { authProfile } = useAuth();
    const { sendTracker } = useTracker();
    const searchParams = useSearchParams();
    const isMobile = useMediaQuery('(max-width: 768px)');

    const trackerUser = {
        user_id: authProfile?.uuid,
        email: authProfile?.email,
        name: authProfile?.name,
        phone: authProfile?.phone_number,
    };

    const isAssetBank = useMemo(() => {
        return additionalFlags?.find(
            (additionalFlag) =>
                additionalFlag.label === DEFAULT_NPL_FLAG &&
                additionalFlag.active,
        );
    }, [additionalFlags]);

    const sortAdditionalFlags = useMemo(() => {
        if (additionalFlags && additionalFlags.length > 0) {
            const filterDuplicateAdditionalFlag = additionalFlags
                .filter(
                    (item, index, self) =>
                        index === self.findIndex((t) => t.label === item.label),
                )
                .filter(
                    ({ label }) =>
                        !under1YearBadgesValue.includes(label.toLowerCase()),
                );

            const addURLOnLabel = filterDuplicateAdditionalFlag.map((flag) => {
                const flagParams = new URLSearchParams(searchParams);
                flagParams.set('quickLabel', flag?.label || '');

                return {
                    ...flag,
                    url: `${url}?${flagParams.toString()}`,
                };
            });

            return addURLOnLabel
                .filter((flag) => flag.active)
                .filter((flag) => flag.label !== 'ASET BANK')
                .filter((flag) => flag.category !== 'Special Label')
                .sort((a, b) => a.sequence - b.sequence)
                .slice(0, 3);
        }
        return null;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [additionalFlags]);

    const [_, setSearchbarTokenStorage] = useLocalStorageState<
        {
            id: string;
            name: string;
            type: SuggestionKind | AdditionalSuggestionKind;
        }[]
    >(SEARCHBAR_TOKENS_KEY, []);

    const sendClickableLabelTracker = (label: string): void => {
        const parts = url?.split('/') || '';
        const originId = parts[parts.length - 2];
        const trackerParam = {
            ...trackerUser,
            current_page_type: 'SRP',
            event_metric: 'click',
            feature: 'Clickable Label',
            feature_detail: label,
            feature_listing_class: 'Secondary',
            feature_object_id: originId,
            feature_object_type: 'Listing',
            platform: isMobile ? 'Mobile' : 'Desktop',
            source_category: 'Portal',
            source_data: 'Web',
        };

        sendTracker(SegmentTrackerEventEnum.LabelClicked, trackerParam);
    };

    const getAdditionalFlags = (
        categoryFilter: (flag: any) => boolean,
    ): (Flag & { url: string })[] => {
        if (additionalFlags && additionalFlags.length > 0 && ref) {
            const filterDuplicateAdditionalFlag = additionalFlags
                .filter(
                    (item, index, self) =>
                        index === self.findIndex((t) => t.label === item.label),
                )
                .filter(
                    ({ label }) =>
                        !under1YearBadgesValue.includes(label.toLowerCase()),
                )
                .filter(categoryFilter);

            const addURLOnLabel = filterDuplicateAdditionalFlag.map((flag) => {
                if (typeof window !== 'undefined') {
                    try {
                        const currentUrl = new URL(window.location.href);
                        currentUrl.searchParams.set('quickLabel', flag?.label);
                        const updatedUrl = currentUrl.toString();
                        return { ...flag, url: updatedUrl };
                    } catch (error) {
                        // Fallback if URL parsing fails during SSR
                        console.warn(
                            'Failed to parse URL in PropertyInfoChips:',
                            error,
                        );
                        return {
                            ...flag,
                            url: `${url}?quickLabel=${flag?.label}`,
                        };
                    }
                }
                // Fallback for SSR
                return { ...flag, url: `${url}?quickLabel=${flag?.label}` };
            });

            return addURLOnLabel
                .filter((flag) => flag.active)
                .filter((flag) => flag.label !== 'ASET BANK')
                .sort((a, b) => a.sequence - b.sequence)
                .slice(0, 3);
        }
        return [];
    };

    const isApartment = ['apartemen', 'apartment'].includes(
        propertyType?.formattedValue.toLowerCase(),
    );

    const under1yearBadge = useMemo(() => {
        const flags = (additionalFlags || []).map(({ label }) =>
            label.toLowerCase(),
        );

        if (
            flags.includes('newly renovated') ||
            flags.includes('baru direnovasi')
        )
            return 'renovation';

        if (flags.includes('newly built') || flags.includes('baru dibangun'))
            return 'build';

        return undefined;
    }, [additionalFlags]);

    const specialLabels = useMemo(
        () => getAdditionalFlags((flag) => flag.category === 'Special Label'),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [additionalFlags],
    );

    return (
        <div
            className={cn(
                {
                    'relative flex items-center gap-x-2 z-[3] overflow-x-auto no-scrollbar whitespace-nowrap': true,
                    'px-3': specialFlag?.isNpl,
                },
                className,
            )}
        >
            {+propertyType.value === PropertyTypeEnum.newLaunch ? (
                <Chip type='official' className={chipSize[size]} />
            ) : (
                <Chip
                    theme={isGts ? 'primary' : 'secondary'}
                    label={propertyType.formattedValue}
                    className={chipSize[size]}
                />
            )}
            {SubscriptionTierConstant[subscriptionTierId] === 'premier' && (
                <p
                    className={`bg-amber-400 text-black inline-block py-1.5 px-2 rounded-2xl ${chipSize[size]}`}
                >
                    Premier
                </p>
            )}
            {SubscriptionTierConstant[subscriptionTierId] === 'featured' && (
                <p
                    className={`bg-teal-500 text-accent inline-block py-1.5 px-2 rounded-2xl ${chipSize[size]}`}
                >
                    Featured
                </p>
            )}

            {cobrokeBadge?.badgeType && <CobrokeBadge {...cobrokeBadge} />}

            {isApartment && attributes?.furnishing && (
                <div
                    data-test-id='badge-depth'
                    className='bg-[#5982d5] py-1.5 px-2 rounded-3xl text-[10px] text-white'
                >
                    {attributes?.furnishing.formattedValue}
                </div>
            )}

            {under1yearBadge && (
                <Under1YearBadge
                    type={under1yearBadge}
                    lang={locale as LangType}
                    onClick={(): void => {
                        sendClickableLabelTracker(
                            under1yearBadge === 'build'
                                ? 'Baru Dibangun'
                                : 'Baru Direnov',
                        );
                    }}
                />
            )}

            {priceType?.value === '0' && specialFlag?.isUnderNjop && (
                <ChipUnderNjop className={chipSize[size]} />
            )}

            {isAssetBank && <ChipAssetBank className={chipSize[size]} />}

            {specialLabels &&
                specialLabels.map(({ icon, label, sequence }) => {
                    return (
                        <span
                            className='flex h-[20px] px-2 py-1 justify-center items-center gap-[2px] rounded-full bg-[#B6BAC2] bg-blend-overlay bg-[linear-gradient(270deg,rgba(255,255,255,0)_18.43%,rgba(255,255,255,0.5)_85.77%)] no-underline'
                            key={sequence}
                        >
                            <img
                                src={icon || ''}
                                className='w-4 h-4 object-contain'
                                alt={label}
                                decoding='async'
                                loading='lazy'
                            />
                            <span className='text-3xs font-medium text-[#0d1a35]'>
                                {label}
                            </span>
                        </span>
                    );
                })}

            {!isAssetBank &&
                sortAdditionalFlags &&
                sortAdditionalFlags.map(({ label, sequence, url }, index) => {
                    if (
                        label.toLowerCase().includes('dekat pantai') ||
                        label.toLowerCase().includes('near pantai')
                    ) {
                        return (
                            <div
                                key={`${label}-${sequence}`}
                                className={`bg-gradient-to-r from-[#F6F08A] to-[#f7d279] inline-flex h-6 justify-center items-center gap-0.5 shrink-0 text-accent font-medium leading-4 px-2 py-1.5 rounded-full ${chipSize[size]}`}
                            >
                                <svg
                                    xmlns='http://www.w3.org/2000/svg'
                                    width='16'
                                    height='17'
                                    viewBox='0 0 16 17'
                                    fill='none'
                                >
                                    <path
                                        d='M8.0499 12.7772L8.05038 12.7772L10.2144 12.7778C10.3382 12.7783 10.6483 12.8162 10.6396 12.6149C10.6335 12.4743 10.2225 12.1187 10.1069 12.0195C9.51026 11.5061 8.70801 11.106 7.90075 11.145C6.98228 11.1763 6.14207 11.6579 5.52254 12.3185C5.15823 12.7069 5.24296 12.7793 5.76101 12.7767L8.0499 12.7772Z'
                                        fill='currentColor'
                                    />
                                    <path
                                        d='M11.9805 3.46463C11.9897 3.44025 11.9805 3.40063 11.9592 3.37626C11.9348 3.34578 11.8708 3.33055 11.7794 3.33359C11.6118 3.33969 11.5722 3.34578 11.3345 3.43111C10.7098 3.65051 10.271 4.19292 10.1186 4.92731C10.0973 5.02787 10.0638 5.11624 10.0455 5.12234C10.0242 5.12843 9.98456 5.08882 9.95713 5.03397C9.64631 4.46718 9.23493 4.10455 8.77784 4.00094C8.5432 3.94914 8.28113 3.9339 8.21409 3.97047C8.07392 4.04361 8.12877 4.12893 8.66204 4.67744C9.24712 5.27775 9.36901 5.41183 9.36901 5.46059C9.36901 5.50629 9.34463 5.50325 9.12218 5.43316C8.54625 5.24728 7.95508 5.35698 7.51627 5.73179C7.29382 5.91767 7.24811 5.99386 7.29382 6.09137L7.32734 6.16755L8.14096 6.1645H9.02772C9.14351 6.1645 9.10085 6.24678 8.9363 6.34429C8.67119 6.49665 8.33598 6.81052 8.15315 7.07259C8.06173 7.20362 7.94289 7.5053 7.90327 7.69728C7.83014 8.066 7.85756 8.55661 7.95812 8.66935C8.02212 8.74249 8.15924 8.68459 8.2659 8.54137C8.61024 8.06295 9.28064 7.23714 9.65545 6.81966C9.96932 6.47532 10.0546 6.39609 10.2161 6.29858C10.4356 6.1645 10.6275 6.18888 11.2583 6.43876C11.3497 6.47532 11.4411 6.50884 11.4564 6.51189C11.496 6.52408 12.011 6.76786 12.462 6.98726C12.6631 7.08782 13.0501 7.29199 13.3213 7.44435C13.5956 7.59367 13.8424 7.71861 13.8759 7.71861C14.0496 7.71861 14.0405 7.41693 13.8515 7.03907C13.5255 6.38086 12.8673 5.94815 11.9927 5.81711C11.7002 5.77445 11.6545 5.75617 11.7032 5.70741C11.7276 5.68304 12.8977 5.35088 13.4371 5.21071C13.7784 5.12539 13.6992 4.95779 13.2086 4.73838C12.9282 4.61344 12.718 4.56774 12.401 4.56774C11.9836 4.56469 11.7184 4.63478 11.3223 4.85418C11.1059 4.97302 10.981 4.99435 11.0359 4.90598C11.0481 4.88465 11.2644 4.56164 11.5112 4.18683C11.7611 3.81201 11.9714 3.48596 11.9805 3.46463Z'
                                        fill='currentColor'
                                    />
                                    <path
                                        d='M5.0358 6.74958C4.90781 6.12184 4.533 5.69218 3.94488 5.49106C3.74376 5.42402 3.65234 5.41792 3.63101 5.47277C3.62187 5.49715 3.78642 5.79883 3.99668 6.14927C4.20389 6.49665 4.36844 6.78919 4.3593 6.79833C4.35016 6.80443 4.2496 6.75262 4.13685 6.67644C3.56092 6.29858 3.02765 6.25897 2.44562 6.55151C2.1866 6.67949 2.21708 6.70387 2.96366 6.97203C3.32628 7.10306 3.68586 7.24019 3.76204 7.27371L3.89917 7.33465L3.60968 7.35598C3.28057 7.38341 3.03679 7.44131 2.8052 7.55101C2.61322 7.64243 2.38163 7.8344 2.40905 7.87707C2.42124 7.89535 2.41515 7.90144 2.39686 7.88926C2.31764 7.8405 1.98244 8.41034 2.00072 8.56575C2.019 8.7303 2.0769 8.72116 2.70159 8.46824C3.0246 8.33416 3.37199 8.19703 3.47255 8.15741C4.09419 7.92278 4.43548 7.80698 4.58175 7.77651C4.83163 7.7247 4.9261 7.77346 5.1973 8.10866C5.50508 8.48042 6.02006 9.16911 6.37659 9.68105C6.45887 9.79989 6.48934 9.82427 6.55943 9.81513C6.67827 9.80294 6.71789 9.69628 6.71789 9.39156C6.71789 8.93142 6.56248 8.5688 6.21204 8.22141C6.0932 8.10561 5.94693 7.98067 5.88598 7.94715C5.82199 7.91363 5.77324 7.87402 5.77324 7.85878C5.77324 7.82831 6.12977 7.84659 6.74531 7.90754C7.10184 7.94411 7.12317 7.94106 7.16279 7.88621C7.18717 7.85573 7.20545 7.81612 7.20545 7.80088C7.20545 7.73384 6.84587 7.42912 6.65999 7.34379C6.49239 7.26457 6.41316 7.24628 6.18462 7.23714C6.01092 7.23105 5.85246 7.24323 5.7519 7.27066C5.55383 7.32246 5.54469 7.28894 5.70924 7.13963C6.27603 6.63074 6.62647 6.29554 6.62647 6.26202C6.62647 6.20412 6.50458 6.16755 6.3126 6.16755C6.09929 6.16755 5.87075 6.2285 5.7001 6.32906C5.52945 6.42657 5.23996 6.74044 5.19121 6.88061C5.16988 6.93851 5.13636 6.98726 5.11807 6.98726C5.09979 6.98726 5.06322 6.88061 5.0358 6.74958Z'
                                        fill='currentColor'
                                    />
                                    <path
                                        d='M12.4833 9.62315C12.1755 8.6724 11.5448 7.68204 10.8805 7.1122C10.7799 7.02688 10.6885 6.95679 10.6763 6.95679C10.661 6.95679 10.6367 6.98117 10.6214 7.01164C10.5971 7.0543 10.6214 7.12744 10.722 7.32551C10.9719 7.80698 11.1578 8.38901 11.2736 9.02893C11.3345 9.37023 11.3589 10.2966 11.3162 10.6592C11.2735 11.0432 11.1456 11.6404 11.0298 11.9848C10.9749 12.1585 10.9353 12.3139 10.9414 12.3261C10.9505 12.3383 10.9475 12.3505 10.9323 12.3505C10.9201 12.3505 10.8744 12.4358 10.8347 12.5394C10.7921 12.646 10.722 12.8136 10.6763 12.9111C10.6153 13.0391 10.6001 13.1031 10.6214 13.1306C10.6732 13.1945 11.0359 13.2372 11.496 13.2311C12.2791 13.222 12.59 13.1245 12.7515 12.838C12.8277 12.7039 12.8277 12.6917 12.8277 11.9848C12.8277 11.0279 12.7362 10.4063 12.4833 9.62315Z'
                                        fill='currentColor'
                                    />
                                    <path
                                        d='M4.46596 8.92228C4.51471 8.76382 4.54823 8.61755 4.53909 8.60232C4.4751 8.49566 4.29226 8.66631 4.02106 9.08683C3.5853 9.76028 3.3659 10.5739 3.3659 11.4972C3.3659 11.7562 3.37808 12.0579 3.39332 12.1676C3.40856 12.2773 3.43294 12.4327 3.44512 12.5181C3.49083 12.838 3.60663 13.0087 3.85346 13.1214C3.96925 13.1763 4.06067 13.1854 4.46291 13.1915C5.21558 13.2037 5.26434 13.1793 5.07846 12.8807C4.89562 12.5912 4.55433 11.8537 4.55433 11.7471C4.55433 11.7334 4.55432 11.7562 4.55432 11.7334C4.55432 11.7105 4.52004 11.6534 4.52004 11.642C4.52004 11.6305 4.50862 11.6161 4.49034 11.5673C4.40806 11.354 4.3075 10.7842 4.28922 10.4154C4.28007 10.1808 4.28007 10.0178 4.28007 9.98501V9.88217V9.87302C4.28007 9.68409 4.37149 9.20872 4.46596 8.92228Z'
                                        fill='currentColor'
                                    />
                                </svg>
                                <span>{label}</span>
                            </div>
                        );
                    }

                    if (url && !enableQuickLabelChip) {
                        return (
                            <div
                                className={`group bg-white hover:bg-[#f3f7ff] inline-flex h-6 justify-center items-center gap-0.5 shrink-0 text-primary font-medium leading-4 decoration-solid px-2 py-1.5 rounded-full ${chipSize[size]}`}
                                key={`${label}-${sequence}`}
                            >
                                <span>{label}</span>
                            </div>
                        );
                    }

                    return (
                        url &&
                        enableQuickLabelChip && (
                            <a
                                data-test-id={`srp-listing-quick-label-${index + 1}`}
                                className={`group bg-white hover:bg-[#f3f7ff] inline-flex h-6 justify-center items-center gap-0.5 shrink-0 text-primary font-medium leading-4 underline underline-offset-2 decoration-solid px-2 py-1.5 rounded-full ${chipSize[size]}`}
                                key={`${label}-${sequence}`}
                                href={url}
                                onClick={(e): void => {
                                    sendClickableLabelTracker(label);
                                    setSearchbarTokenStorage((prev) => [
                                        {
                                            id: AdditionalSuggestionKind.QuickLabel,
                                            name: label,
                                            type: AdditionalSuggestionKind.QuickLabel,
                                        },
                                        ...prev,
                                    ]);
                                    e.stopPropagation();
                                }}
                            >
                                <span>{label}</span>
                                <svg
                                    width='13'
                                    height='13'
                                    viewBox='0 0 13 13'
                                    fill='none'
                                    xmlns='http://www.w3.org/2000/svg'
                                    className='hidden group-hover:block'
                                >
                                    <path
                                        d='M3.77111 2.39073L7.67486 6.45723V5.93748L3.77111 10.004C3.62749 10.1536 3.63236 10.391 3.78199 10.5346C3.93161 10.6782 4.16899 10.6734 4.31261 10.5237L8.21636 6.45723C8.35586 6.3121 8.35586 6.0826 8.21636 5.93748L4.31261 1.87098C4.16899 1.72135 3.93124 1.71648 3.78199 1.8601C3.63274 2.00373 3.62749 2.2411 3.77111 2.39073Z'
                                        fill='#2951A3'
                                    />
                                </svg>
                            </a>
                        )
                    );
                })}
            {children}
        </div>
    );
};

export default PropertyInfoChips;
